import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { addBidChoiceEvent, fetchLatestBidChoiceEventByReferenceNumberAndEid } from "../../api/bid";
import { fetchCurrentEmployeeMonthlyScheduleMap } from "../../api/scheduleEditor.js";
import PageLoading from "../../components/PageLoading";
import BidChoicesTable from "../../components/bid/BidChoicesTable";
import BidTimer from "../../components/bid/BidTimer";
import BidInfoDialog from "../../components/fundamentals/BidInfoDialog.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../../components/fundamentals/SuccessDialog";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { BID_STATUS, BID_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { getBidName, getDisabledDatesBasedOnSchedule, getNonEmptyChoices } from "../../util/bid/timeOffBidUtils.js";

import { getEidFromHref, getReferenceNumberFromHref } from "../../util/bid/shiftBidUtils.js";
import { BREADCRUMBS_TITLE, CANCEL_WARNING_MESSAGE_ACTION_TYPE, FEATURES, HOME_PATH } from "../../util/constants.js";
import { convertMonthStringToInteger } from "../../util/dateTimeFormatter.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  FREEDAY_BID_CHOICE_CONFIRMATION_MESSAGE,
  FREEDAY_BID_CHOICE_CONFIRMATION_TITLE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import FreedayBidPDFExport from "./FreedayBidPDFExport";

const useStyles = makeStyles((theme) => ({
  lastSubmitted: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  addChoice: {
    margin: "15px",
  },
}));

const FreedayBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidChoicesLoading, setIsBidChoicesLoading] = useState(true);
  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");

  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");

  const [isReadOnly, setIsReadOnly] = useState(false);

  // Schedule state
  const [disabledDates, setDisabledDates] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);

  let history = useHistory();

  useEffect(() => {
    fetchLatestFreedayBidChoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    if (bidChoiceData) {
      fetchSchedule(bidChoiceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidChoiceData]);

  const fetchSchedule = ({ eid, station, schedYear, schedMonth }) => {
    if (eid && station && schedYear && schedMonth) {
      setIsScheduleLoading(true);
      fetchCurrentEmployeeMonthlyScheduleMap(eid, station, schedYear, convertMonthStringToInteger(schedMonth))
        .then((res) => res.json())
        .then((response) => {
          if (response?.event) {
            const disabledDates = getDisabledDatesBasedOnSchedule({
              scheduledMap: response.event,
              station: station,
              bidType: BID_TYPE.FREEDAY,
            });
            setDisabledDates(disabledDates);
            setIsScheduleLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((err) => {
          setIsScheduleLoading(false);
          console.error(`Bid Error: %j`, err);
        });
    }
  };

  const fetchLatestFreedayBidChoices = () => {
    const eid = getEidFromHref();
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          setBidChoiceData(bidChoiceEvent);
          setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          setInitialSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
          setLastUpdatedByLastName(response.event?.modifiedByLastName);

          if (bidChoiceEvent.status === BID_STATUS.CLOSED) {
            setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : []);
            setIsReadOnly(true);
          }

          setIsPageLoading(false);
          setIsBidChoicesLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    setIsSubmitLoading(true);
    console.log("Saving bid choice data...");
    let payload = buildBidChoiceEvent(prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices }), userSession);
    addBidChoiceEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error("Error: ", result.message);
          setSubmitErrors(result.message);
          setIsSubmitLoading(false);
          setIsSubmitDialogOpen(false);
          setIsSubmitResponseDialogOpen(true);
        } else {
          console.log("Success!");
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
          setIsSubmitResponseDialogOpen(true);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsSubmitLoading(false);
      });
  };

  const handleClickSubmitDialogOpen = () => {
    setSelectedChoices(getNonEmptyChoices(selectedChoices));
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (!isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so re-direct to my bids
      history.push(getMyBidsPath());
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([""]);
    setIsClearAllDialogOpen(false);
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const getMyBidsPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.MY_BIDS).path;
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const renderDesktopNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <FreedayBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                }}
                variant="contained"
                color="primary"
                onClick={() =>
                  history.push({
                    pathname: getMyBidsPath(),
                  })
                }
              >
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <FreedayBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="contained" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Box>
            <Box sx={{ paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={
                  bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN ||
                  isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))
                }
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  const handleAddChoice = () => {
    const existingChoices = [...selectedChoices];
    setSelectedChoices([...existingChoices, ""]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${selectedChoices.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
  };

  const renderSubmitFreedayBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You can continue to submit choices up until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting choices for{" "}
                    <b>
                      {selectedChoices?.length} day{selectedChoices?.length > 1 && "s"}
                    </b>
                    .
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again. You can keep editing
                    your choices until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderBidChoices = () => {
    return (
      <>
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray2",
            paddingLeft: "1rem",
          }}
        >
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {getNonEmptyChoices(selectedChoices).length}
              {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
            </Typography>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
            <Button sx={{ fontWeight: "bold", textDecoration: "underline" }} onClick={handleClickClearAllDialogOpen}>
              Clear All
            </Button>
            <ConfirmationDialog
              isDialogOpen={isClearAllDialogOpen}
              title={BID_FORM_CLEAR_ALL}
              message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
              handleNoButtonClicked={handleClickCancelClearAllDialogClose}
              handleYesButtonClicked={handleClearAllChoices}
              noTitle="Cancel"
              yesTitle="Clear All"
            />
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            borderBottom: 0,
          }}
        >
          <Box sx={{ width: "inherit" }}>
            {
              <BidChoicesTable
                selectedChoices={selectedChoices}
                setSelectedChoices={setSelectedChoices}
                isDesktopView={true}
                isBidChoicesLoading={isBidChoicesLoading}
                bidChoiceData={bidChoiceData}
                disabledDates={disabledDates}
                bidType={bidChoiceData.bidType}
              />
            }
            <Divider />
            <Button
              className={classes.addChoice}
              variant="contained"
              onClick={handleAddChoice}
              disabled={selectedChoices.some((choice) => choice === "") ? true : false}
            >
              Add Another Choice
            </Button>
            <Divider />
          </Box>
        </Grid>
      </>
    );
  };

  const renderMobileNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <>
            <Grid container justifyContent="space-between" direction="row" alignItems="center" sx={{ margin: "5px" }}>
              <Grid item xs={4} sx={{ padding: 1, display: "flex", justifyContent: "start" }}>
                <Box sx={{ paddingTop: "10px" }}>
                  <FreedayBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
                </Box>
              </Grid>
              <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
                <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{
                      width: "100px",
                    }}
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      history.push({
                        pathname: getMyBidsPath(),
                      })
                    }
                  >
                    Close
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Grid>
            <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
              <Button
                sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={
                  bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN ||
                  isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))
                }
              >
                Submit
              </Button>
            </Grid>
          </>
        )}
      </>
    );
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_BIDS, getBidName(bidChoiceData)];
  const breadcrumbsLinks = [HOME_PATH, `/${FEATURES.MY_BIDS}`];
  const renderDesktopFreedayBidChoicesPage = () => {
    if (isPageLoading || isScheduleLoading) {
      return <PageLoading />;
    }
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={
            !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))
          }
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
        />
        {/* Title & Cancel/Submit Buttons */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
              <Grid item>
                <Typography variant="h5" component="div" align="left">
                  {getBidName(bidChoiceData)} Freeday Bid
                </Typography>

                {getLastSubmittedTimestamp() !== "" &&
                lastUpdatedByFirstName &&
                lastUpdatedByLastName &&
                lastUpdatedByFirstName !== "" &&
                lastUpdatedByLastName !== "" ? (
                  <Grid className={classes.lastSubmitted}>
                    {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                  </Grid>
                ) : getLastSubmittedTimestamp() !== "" ? (
                  <Grid
                    className={classes.lastSubmitted}
                  >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
                ) : (
                  ""
                )}

                <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                  {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                    <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                      <BidTimer
                        windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                        station={bidChoiceData.station}
                      />
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Stack>
              <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                {renderDesktopNavBtnsForBidForm()}
              </Grid>
            </Stack>
          </Grid>
          <Box
            sx={{
              width: "100%",
              margin: "auto",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              marginTop: "1rem",
            }}
          >
            {isReadOnly ? (
              <>
                <Grid
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid sx={{ height: "40px", alignContent: "center" }}>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {selectedChoices ? selectedChoices.length : 0} {`Choice${selectedChoices?.length > 1 ? "s" : ""}`}{" "}
                      Entered
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 200px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={true}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>{renderBidChoices()}</>
            )}
          </Box>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderMobileFreedayBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {/* Title */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "1rem", paddingTop: "20px" }}>
              <Typography variant="h5" component="div" align="left">
                {getBidName(bidChoiceData)} Freeday Bid
              </Typography>

              {getLastSubmittedTimestamp() !== "" &&
              lastUpdatedByFirstName &&
              lastUpdatedByLastName &&
              lastUpdatedByFirstName !== "" &&
              lastUpdatedByLastName !== "" ? (
                <Grid className={classes.lastSubmitted}>
                  {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                </Grid>
              ) : getLastSubmittedTimestamp() !== "" ? (
                <Grid
                  className={classes.lastSubmitted}
                >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
              ) : (
                ""
              )}

              <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                  <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "12px" }}>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <AccessTimeIcon fontSize="12px" />
                      </Grid>
                      <Grid item xs={11.2} sm={11.2} style={{ fontWeight: "bold", fontSize: "10px" }}>
                        <BidTimer
                          windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                          station={bidChoiceData.station}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%", margin: "auto", marginTop: "10px" }}>
            {isReadOnly ? (
              <>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {selectedChoices ? selectedChoices.length : 0} {`Choice${selectedChoices?.length > 1 ? "s" : ""}`}{" "}
                      Entered
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={true}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Divider />
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getNonEmptyChoices(selectedChoices).length}
                      {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    borderBottom: 0,
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
                <Divider />
                <Button
                  className={classes.addChoice}
                  variant="contained"
                  onClick={handleAddChoice}
                  disabled={selectedChoices.some((choice) => choice === "") ? true : false}
                >
                  Add Another Choice
                </Button>
                <Divider />
              </>
            )}
          </Box>

          <Grid
            container
            sx={{
              marginTop: "5px",
              bottom: 0,
              position: "fixed",
              backgroundColor: "secondary.swaWhite",
              zIndex: 999,
            }}
          >
            {renderMobileNavBtnsForBidForm()}
          </Grid>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderDialogs = () => {
    return (
      <>
        <ConfirmationDialog
          isDialogOpen={isExitDialogOpen}
          title="Cancel"
          message={BID_FORM_CANCEL_MESSAGE}
          handleNoButtonClicked={handleClickCancelDialogClose}
          redirectTo={getMyBidsPath()}
        />
        {renderSubmitFreedayBidChoiceDialog()}
        <BidInfoDialog
          isDialogOpen={isGuideDialogOpen}
          title="Guide"
          handleCloseClicked={handleCloseClickedGuideDialog}
          isDesktopView={props.isDesktopView}
          eid={userSession.eid}
          bidChoiceData={bidChoiceData}
          lastSubmittedTimestamp={getLastSubmittedTimestamp()}
        />
        <SuccessDialog
          isSuccessDialogOpen={isSubmitResponseDialogOpen}
          title={FREEDAY_BID_CHOICE_CONFIRMATION_TITLE}
          message={FREEDAY_BID_CHOICE_CONFIRMATION_MESSAGE}
          hasError={submitErrors !== ""}
          error={BID_CHOICE_SAVE_ERROR_MESSAGE}
          redirectTo={submitErrors === "" && getMyBidsPath()}
          handleOKClick={() => setIsSubmitResponseDialogOpen(false)}
        />
      </>
    );
  };

  return (
    <div className="App-contents enable-scroll" data-testid="freeday-bid-choices-page">
      {props.isDesktopView ? renderDesktopFreedayBidChoicesPage() : renderMobileFreedayBidChoicesPage()}
    </div>
  );
};

export default FreedayBidChoices;
